/**
 * Sibling Swatches Web Component
 *
 * Handles tab switching for the cross-product colour swatch navigation.
 * Follows Dawn's Web Component pattern — no jQuery, no external dependencies.
 *
 * Behavior:
 * - Clicking a tab shows the corresponding swatch panel via opacity fade (150ms).
 * - Persists the active tab to sessionStorage across cross-product navigation.
 * - Active swatch (current product) is non-clickable — prevents page reload.
 * - On swatch hover, updates the colour label to show the hovered colour name.
 * - Restores the active colour name when hover leaves.
 * - Overflow cap: hides swatches beyond data-overflow-count, shows +N more button.
 * - Mobile: active tab is scrolled into view on activation.
 */

if (!customElements.get('sibling-swatches')) {
  class SiblingSwatches extends HTMLElement {
    constructor() {
      super();
      this._onTabClick    = this._onTabClick.bind(this);
      this._onSwatchEnter = this._onSwatchEnter.bind(this);
      this._onSwatchLeave = this._onSwatchLeave.bind(this);
      this._onSwatchClick = this._onSwatchClick.bind(this);
    }

    connectedCallback() {
      this.tabs     = this.querySelectorAll('.sibling-swatches__tab');
      this.panels   = this.querySelectorAll('.sibling-swatches__panel');
      this.swatches = this.querySelectorAll('.sibling-swatches__swatch');

      // Store the initial label text (current product's colour name)
      const labelEl = this._getLabelEl();
      this._activeLabel = labelEl ? labelEl.textContent.trim() : '';

      // Apply overflow cap if enabled
      if (this.dataset.overflowCap === 'true') {
        this._applyOverflowCap();
      }

      // Restore previously selected tab (survives cross-product navigation)
      this._restoreTab();

      this.tabs.forEach(tab => {
        tab.addEventListener('click', this._onTabClick);
      });

      this.swatches.forEach(swatch => {
        swatch.addEventListener('click',      this._onSwatchClick);
        swatch.addEventListener('mouseenter', this._onSwatchEnter);
        swatch.addEventListener('mouseleave', this._onSwatchLeave);
        swatch.addEventListener('focusin',    this._onSwatchEnter);
        swatch.addEventListener('focusout',   this._onSwatchLeave);
      });
    }

    disconnectedCallback() {
      this.tabs.forEach(tab => tab.removeEventListener('click', this._onTabClick));
      this.swatches.forEach(swatch => {
        swatch.removeEventListener('click',      this._onSwatchClick);
        swatch.removeEventListener('mouseenter', this._onSwatchEnter);
        swatch.removeEventListener('mouseleave', this._onSwatchLeave);
        swatch.removeEventListener('focusin',    this._onSwatchEnter);
        swatch.removeEventListener('focusout',   this._onSwatchLeave);
      });
    }

    // -------------------------------------------------------------------------
    // Overflow cap — inject +N more button per panel
    //
    // data-overflow="true" is now emitted server-side by Liquid (sibling-swatch-item.liquid),
    // so swatches are hidden from first paint. This method only reads the count of
    // already-hidden swatches and injects the expand button — no DOM mutation of swatch visibility.
    // -------------------------------------------------------------------------

    _applyOverflowCap() {
      this.panels.forEach(panel => {
        // Count swatches already marked overflow by server-rendered Liquid
        const overflowSwatches = Array.from(panel.querySelectorAll('.sibling-swatches__swatch[data-overflow="true"]'));
        const overflowCount = overflowSwatches.length;
        if (overflowCount <= 0) return;

        // Render a +N more button
        const btn = document.createElement('button');
        btn.className = 'sibling-swatches__overflow-btn';
        btn.type = 'button';
        btn.textContent = `+${overflowCount}`;
        btn.setAttribute('aria-label', `Show ${overflowCount} more colours`);

        btn.addEventListener('click', () => {
          panel.querySelectorAll('[data-overflow="true"]').forEach(sw => {
            sw.removeAttribute('data-overflow');
          });
          btn.remove();
        }, { once: true });

        const list = panel.querySelector('.sibling-swatches__swatch-list');
        if (list) list.appendChild(btn);
      });
    }

    // -------------------------------------------------------------------------
    // Active swatch click — block navigation to self
    // -------------------------------------------------------------------------

    _onSwatchClick(event) {
      const swatch = event.currentTarget;

      if (swatch.classList.contains('sibling-swatches__swatch--active')) {
        event.preventDefault();
        return;
      }

      // Persist the currently visible tab so the destination page can restore it
      const activeTab = this.querySelector('.sibling-swatches__tab--active, .sibling-swatches__tab[aria-selected="true"]');
      if (activeTab) {
        try {
          sessionStorage.setItem('siblingSwatches_activeTab', activeTab.textContent.trim());
        } catch (_) {}
      }
    }

    // -------------------------------------------------------------------------
    // Tab switching with fade transition + mobile scroll
    // -------------------------------------------------------------------------

    _onTabClick(event) {
      const selectedTab   = event.currentTarget;
      const targetPanelId = selectedTab.getAttribute('aria-controls');

      this._activateTab(selectedTab, targetPanelId);

      // Do NOT write sessionStorage here. Tab clicks are local UI changes —
      // persisting them would cause every subsequent product page to open on
      // this tab even when the user navigates there from an unrelated context.
      // sessionStorage is only written on swatch clicks (immediately before
      // navigation), making it a one-shot handoff to the destination page.
    }

    _activateTab(selectedTab, targetPanelId, skipScroll = false) {
      // Deactivate all tabs
      this.tabs.forEach(tab => {
        tab.setAttribute('aria-selected', 'false');
        tab.classList.remove('sibling-swatches__tab--active');
      });

      // Fade out all panels
      this.panels.forEach(panel => {
        panel.classList.remove('sibling-swatches__panel--active');
      });

      // Activate selected tab
      selectedTab.setAttribute('aria-selected', 'true');
      selectedTab.classList.add('sibling-swatches__tab--active');

      // Fade in target panel
      const targetPanel = this.querySelector(`#${CSS.escape(targetPanelId)}`);
      if (targetPanel) {
        targetPanel.classList.add('sibling-swatches__panel--active');
      }

      // Scroll active tab into view on mobile — skipped during restore to avoid
      // a smooth-scroll animation on every page load.
      if (!skipScroll) {
        try {
          selectedTab.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
        } catch (_) {}
      }
    }

    // -------------------------------------------------------------------------
    // Restore tab from sessionStorage after cross-product navigation
    // -------------------------------------------------------------------------

    _restoreTab() {
      let savedTab;
      try {
        savedTab = sessionStorage.getItem('siblingSwatches_activeTab');
      } catch (_) {
        // If sessionStorage is unavailable, reveal the component immediately.
        this.removeAttribute('data-restoring');
        return;
      }

      if (!savedTab) {
        // No saved tab — first visit or cleared storage. Reveal immediately.
        this.removeAttribute('data-restoring');
        return;
      }

      // Consume the key immediately — it is a one-shot handoff from the
      // navigation source (swatch click). Removing it here means: if the user
      // navigates away and comes back to any product page without clicking a
      // swatch, no stale tab will be incorrectly restored.
      try {
        sessionStorage.removeItem('siblingSwatches_activeTab');
      } catch (_) {}

      const matchingTab = Array.from(this.tabs).find(
        tab => tab.textContent.trim() === savedTab
      );

      if (matchingTab) {
        const targetPanelId = matchingTab.getAttribute('aria-controls');

        // Suppress panel CSS transitions for the restore switch so the browser
        // never paints the intermediate "All" state — the correct tab appears
        // in one frame with no animated flash. User-initiated clicks still animate
        // because transitions are re-enabled on the next animation frame.
        // skipScroll=true prevents the smooth-scroll animation on every page load.
        this.panels.forEach(p => { p.style.transition = 'none'; });
        this._activateTab(matchingTab, targetPanelId, true);
        requestAnimationFrame(() => {
          this.panels.forEach(p => { p.style.transition = ''; });
          // Remove data-restoring (set server-side) now that the correct tab is visible.
          this.removeAttribute('data-restoring');
        });
      } else {
        // Saved tab name not found in this product's tabs — reveal immediately.
        this.removeAttribute('data-restoring');
      }
    }

    // -------------------------------------------------------------------------
    // Swatch hover — update the colour label
    // -------------------------------------------------------------------------

    _onSwatchEnter(event) {
      const swatch        = event.currentTarget;
      const colorName     = swatch.dataset.colorName;
      const labelTargetId = swatch.dataset.labelTarget;
      if (!colorName || !labelTargetId) return;

      const labelEl = document.getElementById(labelTargetId);
      if (labelEl) labelEl.textContent = colorName;
    }

    _onSwatchLeave(event) {
      const swatch        = event.currentTarget;
      const labelTargetId = swatch.dataset.labelTarget;
      if (!labelTargetId) return;

      const labelEl = document.getElementById(labelTargetId);
      if (labelEl) labelEl.textContent = this._activeLabel;
    }

    _getLabelEl() {
      const id = this.id.replace('SiblingSwatches-', 'siblingSwatchLabel-');
      return document.getElementById(id);
    }
  }

  customElements.define('sibling-swatches', SiblingSwatches);
}
